import { OwnerType } from '~/repository/types/contact-point'

export interface ApiOptions {
  patientId?: string
}

interface ApiConfiguration {
  [key: string]: {
    call: (api: any, options: ApiOptions) => Promise<any>
  }
}

const apiConfigurations: ApiConfiguration = {
  patient: {
    call: async (api: any, options: ApiOptions) => {
      if (!options.patientId) {
        console.warn('Missing patientId for patient')
        return null
      }
      const patient = await api.patients.get(options.patientId)

      return { ...patient, gender: patient.gender === 'male' ? 'Homme' : 'Femme' }
    },
  },
  information: {
    call: async (api: any, options: ApiOptions) => {
      if (!options.patientId) {
        console.warn('Missing patientId for patient information')
        return null
      }
      const { information } = await api.patients.getInformation(options.patientId)

      return information
    },
  },
  livingPlace: {
    call: async (api: any, options: ApiOptions) => {
      if (!options.patientId) {
        console.warn('Missing patientId for livingPlace')
        return null
      }
      const patient = await api.patient.getPatient(options.patientId)

      let places = await api.places.get({ type: OwnerType.person, id: patient.personId as string })
      const livingPlace = places && places[0] ? places[0] : null

      if (!livingPlace) return null
      return { ...livingPlace, address: { ...livingPlace?.address, line: livingPlace?.address?.lines?.join(', ') } }
    },
  },
}

export async function seedSurvey(
  questions: string[],
  answers: Record<string, any>,
  options: ApiOptions,
  api: any,
): Promise<Record<string, any>> {
  const seededAnswers: Record<string, any> = { ...answers }
  const cache: Record<string, any> = {} // Cache to store API call results and avoid repeated calls

  for (const question of questions) {
    if (seededAnswers?.[question]) continue // Skip already answered questions

    const [prefix, ...suffixParts] = question.split('_')
    const suffix = suffixParts.join('.') // Join the rest of the parts to use in data extraction

    if (!cache[prefix] && apiConfigurations[prefix]) {
      try {
        // Cache the result of the API call to avoid repeated calls
        cache[prefix] = await apiConfigurations[prefix].call(api, options)
      } catch (error) {
        console.error(`Error fetching data for ${question}: ${error}`)
      }
    }

    // Use the cached data to get the specific field value
    if (cache[prefix]) {
      const data = cache[prefix]
      const fieldValue = suffix.split('.').reduce((obj, property) => obj?.[property], data) // Safely navigate through the data object
      if (fieldValue !== undefined) {
        seededAnswers[question] = fieldValue
      }
    }
  }

  return seededAnswers
}

interface Element {
  name?: string
  elements?: Element[]
}

export function getSurveyQuestions(elements: Element[]): string[] {
  let names: string[] = []

  // Iterate through each element
  elements.forEach((element) => {
    // Check if the current element has a 'name' property and add it to the names array
    if (element.name) {
      names.push(element.name)
    }

    // If the element has a nested array of elements, recurse through them and concatenate the results
    if (Array.isArray(element.elements)) {
      names = names.concat(getSurveyQuestions(element.elements))
    }
  })

  return names
}

function isLessThanAWeekOld(date: Date) {
  const inputDate = new Date(date)
  const currentDate = new Date()
  const dateAWeekAgo = new Date(currentDate.setDate(currentDate.getDate() - 7))

  return inputDate > dateAWeekAgo
}

export async function seedObservations(patientId: string, observationCodes: string[], answers: any, api: any) {
  const updatedAnswers = { ...answers }
  const observationsResult = await api.observations.getLatestObservationByCode(patientId, observationCodes.join(','))
  observationsResult?.forEach((e: any) => {
    updatedAnswers[e.code] = e.value?.valueBoolean ?? e.value?.valueFloat ?? e.value?.valueInteger ?? e.value?.valueString
  })

  return updatedAnswers
}
